import {AccountType, featureToggles} from 'helpers/constants'
import {get, isEmpty, isNil} from 'lodash'
import {iconNames} from 'components/icon'
import {licencesInSinglePackage, setMainPackage} from 'helpers/logins'
import {featureToggleIsOn} from "helpers/user"

const MILLIS_IN_DAY = 86400000

export const partCodes = {
  lite: 'CG010',
  standard: 'CG010',
  premium: 'CG011',
  purchasing: 'CG012',
  salesPlus: 'CG013',
  hubspotIntegration: 'CG014',
  management: 'CG016',
  shopFloor: 'CG017',
  pod: 'CP301',
  sage50: 'CG019',
  deliveryScheduling: 'CG020',
  wideFormatPrintCalculator: 'CG021',
}

export const PACKAGES_ORDER = [
  partCodes.lite,
  partCodes.premium,
  partCodes.shopFloor,
  partCodes.management,
  partCodes.pod,
]

export const planNames = {
  'CG011': 'Clarity Go Premium',
  'CG010': 'Clarity Go Lite',
  'CG017': 'Shop Floor',
  'CG016': 'Management',
  'CP301': 'Pod',
  'CG012': 'Purchasing',
  'CG013': 'Sales Plus',
  'CG020': 'Delivery Scheduling',
  'CG014': 'Hubspot Integration',
  'CG019': 'sage50',
  'CG021': 'Wide-Format Print Calculator',
}

export const TRIAL_PACKAGES_TO_BE_INCLUDED_IN_CHANGE_PLAN = [
  partCodes.lite,
  partCodes.premium,
  partCodes.shopFloor,
]

export const packagesShownWithLicences = [
  partCodes.purchasing,
  partCodes.salesPlus,
  partCodes.deliveryScheduling,
  partCodes.sage50,
]

export const mainPackages = [
  partCodes.premium,
  partCodes.lite,
]

export const packageIcons = {
  [partCodes.purchasing]: iconNames.purchasingFilled,
  [partCodes.salesPlus]: iconNames.sales,
  [partCodes.deliveryScheduling]: iconNames.delivery,
  [partCodes.sage50]: iconNames.sage,
  [partCodes.hubspotIntegration]: iconNames.hubspot,
  [partCodes.shopFloor]: iconNames.production,
  [partCodes.pod]: iconNames.delivery,
  [partCodes.wideFormatPrintCalculator]: iconNames.pricingCalculator,
}

export const integrationPartCodes = [
  partCodes.hubspotIntegration,
  partCodes.sage50,
]

export const pricingCalculatorPartCodes = [
  partCodes.wideFormatPrintCalculator,
]

export const getAdditionalFeaturePackagePartCodes = (user, partCode) => {
  if (partCode !== partCodes.premium)
    return []

  const packagePartCodes = [
    partCodes.purchasing,
    partCodes.salesPlus,
    partCodes.deliveryScheduling,
    partCodes.sage50,
    partCodes.hubspotIntegration,
    partCodes.shopFloor,
    partCodes.pod,
  ]

  if (featureToggleIsOn(user, featureToggles.PRICING_CALCULATOR))
    packagePartCodes.push(partCodes.wideFormatPrintCalculator)

  return packagePartCodes
}

export const getLicencesInSinglePackage = (licences) => {
  return licences.filter(licence =>
      licencesInSinglePackage.includes(licence.licenceId) || licencesInSinglePackage.includes(licence.id))
}

export const getDaysRemainingOnTrial = (user) => {
  return getDaysRemainingUntilDate(user.trialEndDate, new Date())
}

export const getDaysRemainingUntilDate = (expiryDate, date) => {
  const expiryDay = Math.floor(expiryDate / MILLIS_IN_DAY)
  const baseDay = Math.floor(date.getTime() / MILLIS_IN_DAY)
  return Math.max(0, expiryDay - baseDay)
}

export const isCancellationNoticeRequired = (accountTypeId) => {
  return accountTypeId !== AccountType.TRIAL
      && accountTypeId !== AccountType.SIGN_UP
}

export const findPackageByPartCode = (featurePackages, partCode) => {
  return !isNil(featurePackages)
      ? featurePackages.find(featurePackage => featurePackage.partCode === partCode)
      : null
}

export const findPackageIdByPartCode = (featurePackages, partCode) => {
  return get(findPackageByPartCode(featurePackages, partCode), 'id')
}

export const findPackage = (featurePackages, packageId) => {
  return !isNil(featurePackages)
      ? featurePackages.find(featurePackage => featurePackage.id === packageId)
      : null
}

export const findPackagePartCode = (featurePackages, packageId) => {
  const featurePackage = findPackage(featurePackages, packageId)
  return get(featurePackage, 'partCode')
}

export const findPackagePartCodeForAccountPackage = (featurePackages, accountPackage) => {
  if (isNil(accountPackage))
    return null

  return findPackagePartCode(featurePackages, accountPackage.packageId)
}

export const findPackageName = (featurePackages, packageId) => {
  const featurePackage = findPackage(featurePackages, packageId)
  return get(featurePackage, 'name')
}

export const findPackageNameForAccountPackage = (featurePackages, accountPackage) => {
  if (isNil(accountPackage))
    return ''

  return findPackageName(featurePackages, accountPackage.packageId)
}

export const areAccountPackagesDifferent = (accountPackages1, accountPackages2) =>
    (accountPackages1.length !== accountPackages2.length) ||
    !accountPackages1.every(cp =>
        accountPackages2.some(np => np.packageId === cp.packageId && np.quantity === cp.quantity))

export const getMainFeaturePackageIds = featurePackages => {
  return getMainFeaturePackages(featurePackages).map(featurePackage => featurePackage.id)
}

export const getTrialFeaturePackageIds = featurePackages => {
  return getTrialFeaturePackages(featurePackages).map(featurePackage => featurePackage.id)
}

export const getMainFeaturePackages = (featurePackages) => {
  return featurePackages
      .filter(isMainPackage)
}
export const getTrialFeaturePackages = (featurePackages) => {
  return featurePackages
      .filter(isTrialAccountPackage)
}
export const isMainPackage = (featurePackage) => {
  return mainPackages.includes(featurePackage.partCode)
}

export const isTrialAccountPackage = (featurePackage) => {
  return TRIAL_PACKAGES_TO_BE_INCLUDED_IN_CHANGE_PLAN.includes(featurePackage.partCode)
}

export const getAccountPackagesWithDefault = (user, accountPackages, featurePackages) => {
  return !isEmpty(accountPackages)
      ? accountPackages
      : getDefaultAccountPackages(user, featurePackages)
}

export const getDefaultAccountPackages = (user, featurePackages) => {
  const mainFeaturePackages = getMainFeaturePackages(featurePackages)
  const premiumPackage = findPackageByPartCode(mainFeaturePackages, partCodes.premium)

  return isNil(premiumPackage)
      ? []
      : setMainPackage(user, premiumPackage, featurePackages, [])
}

export const getSelectedMainAccountPackage = (featurePackages, accountPackages) => {
  const mainPackageIds = getMainFeaturePackageIds(featurePackages)
  return accountPackages.find(accountPackage => mainPackageIds.includes(accountPackage.packageId))
}

export const getSelectedTrialAccountPackage = (featurePackages, accountPackages) => {
  const trialPackageIds = getTrialFeaturePackageIds(featurePackages)
  return accountPackages.find(accountPackage => trialPackageIds.includes(accountPackage.packageId))
}

export const getNonMainAccountPackages = (featurePackages, accountPackages) => {
  const mainPackageIds = getMainFeaturePackageIds(featurePackages)
  return accountPackages.filter(accountPackage => !mainPackageIds.includes(accountPackage.packageId))
}

export const getNonTrialAccountPackages = (featurePackages, accountPackages, filteredAccountPackages) => {
  const selectedTrialPackageIds = filteredAccountPackages.map(filteredAccountPackage =>
      filteredAccountPackage.packageId)
  return accountPackages.filter(accountPackage =>
      !selectedTrialPackageIds.includes(accountPackage.packageId))
}

export const createAccountPackage = (featurePackage, templateAccountPackage) => {
  return {
    packageId: featurePackage.id,
    quantity: get(templateAccountPackage, 'quantity', 1),
  }
}

export const capAccountPackageQuantity = (accountPackage, featurePackages, maxQuantity) => {
  const featurePackage = findPackage(featurePackages, accountPackage.packageId)

  return {
    ...accountPackage,
    quantity: isPackageCappedAtMainQuantity(featurePackage)
        ? Math.min(maxQuantity, accountPackage.quantity)
        : accountPackage.quantity,
  }
}

export const updateAccountPackageQuantity = (accountPackage, quantity) => {
  return {
    ...accountPackage,
    quantity,
  }
}

export const disableNonEntitledAdditionalPackages = (user, featurePackages, accountPackages) => {
  const mainAccountPackage = getSelectedMainAccountPackage(featurePackages, accountPackages)
  const mainAccountFeaturePackage = mainAccountPackage
      ? featurePackages.find(featurePackage => featurePackage.id === mainAccountPackage.packageId)
      : null

  const additionalPackages = getAdditionalFeaturePackagesForFeaturePackage(
    user,
    mainAccountFeaturePackage,
    featurePackages)

  const additionalPackageIds = additionalPackages.map(featurePackage => featurePackage.id)

  return accountPackages.map(accountPackage => {
    return {
      ...accountPackage,
      disabled: !shouldKeepAccountPackageEnabled(accountPackage, mainAccountPackage, additionalPackageIds),
    }
  })
}

export const shouldKeepAccountPackageEnabled = (accountPackage, mainAccountPackage, additionalPackageIds) => {
  return (
      accountPackage.packageId === mainAccountPackage.packageId ||
      additionalPackageIds.includes(accountPackage.packageId)
  )
}

export const removeDisabledPackages = (accountPackages) =>
    accountPackages.filter(accountPackage => !accountPackage.disabled)

export const getAdditionalFeaturePackagesForFeaturePackage = (user, featurePackage, featurePackages) => {
  if (isNil(featurePackage))
    return []

  const additionalPartCodes = getAdditionalFeaturePackagePartCodes(user, featurePackage.partCode)

  return !isEmpty(additionalPartCodes)
      ? featurePackages.filter(featurePackage => additionalPartCodes.includes(featurePackage.partCode))
      : []
}

export const findAccountPackageForFeaturePackage = (accountPackages, featurePackageId) => {
  return accountPackages.find(accountPackage => accountPackage.packageId === featurePackageId)
}

export const getMaxPackages = (mainAccountPackage, featurePackage) => {
  const mainAccountPackageQty = get(mainAccountPackage, 'quantity', 0)
  return isPackageCappedAtMainQuantity(featurePackage)
      ? mainAccountPackageQty
      : undefined
}

export const isPackageCappedAtMainQuantity = (featurePackage) => {
  return (
      !isShopFloorPackage(featurePackage) &&
      !isPODPackage(featurePackage)
  )
}

export const isShopFloorPackage = (featurePackage) => {
  return featurePackage.partCode === partCodes.shopFloor
}

export const isPODPackage = (featurePackage) => {
  return featurePackage.partCode === partCodes.pod
}

export const isDeliverySchedulingPackage = (featurePackage) => {
  return featurePackage.partCode === partCodes.deliveryScheduling
}

export const isIntegrationPackage = (featurePackage) => {
  return integrationPartCodes.includes(featurePackage.partCode)
}

export const isPricingCalculatorPackage = (featurePackage) => {
  return pricingCalculatorPartCodes.includes(featurePackage.partCode)
}

export const getPackagesUpdatedUsed = (users, allPackages) => {
  const packageUsage = {}
  const updatedPackages = {}

  users.forEach((user) => {
    const activePackages = get(user, 'activePackages', [])

    activePackages.forEach((activePackage) => {
      const packageId = activePackage.packageId || get(activePackage, 'id', 0)

      if (!packageUsage[packageId]) {
        packageUsage[packageId] = (packageUsage[activePackage.used] || 0) + 1
      } else {
        packageUsage[packageId] += 1
      }
    })
  })

  Object.values(allPackages).map((userPackage) => {
    updatedPackages[userPackage.id] = {
      ...userPackage,
      used: packageUsage[userPackage.id] || 0,
    }
  })

  return updatedPackages
}

export const getPackagesUpdatedQty = (featurePackages, selectedAccountPackages) => {
  const updatedPackages = {}

  Object.values(featurePackages).forEach(featurePackage => {
    const accountPackage = selectedAccountPackages.find(pkg => pkg.packageId === featurePackage.id)

    updatedPackages[featurePackage.id] = {
      ...featurePackage,
      qty: isNil(accountPackage)
          ? 0
          : accountPackage.quantity,
    }
  })

  return updatedPackages
}

export const getPackagesWithAdditional = (accountPackages, selectedPackages, allPackages) => {
  const accountPackageIds = Object.values(accountPackages).map(accountPackage => accountPackage.id)

  for (const selectedPackage of selectedPackages) {
    const {packageId, quantity} = selectedPackage

    if (!accountPackageIds.includes(packageId)) {
      const foundPackage = allPackages.find(pkg => pkg.id === packageId)

      if (foundPackage) {
        accountPackages[packageId] = {
          name: foundPackage.name,
          id: foundPackage.id,
          partCode: foundPackage.partCode,
          used: 0,
          licences: foundPackage.licences,
          qty: quantity,
        }
      }
    }
  }

  return accountPackages
}

export const isMainUserPackage = (featurePackage) => {
  return (
    !isShopFloorPackage(featurePackage) &&
    !isPODPackage(featurePackage) &&
    !isIntegrationPackage(featurePackage) &&
    !isPricingCalculatorPackage(featurePackage)
  )
}

export const isDeliverySchedulingSelectedQuantityLessThanOne = (accountPackages, featurePackages) => {
  const deliverySchedulingId = findPackageIdByPartCode(featurePackages, partCodes.deliveryScheduling)
  const deliverySchedulingAccountPackage = findAccountPackageForFeaturePackage(accountPackages, deliverySchedulingId)
  const deliverySchedulingQuantity = get(deliverySchedulingAccountPackage, 'quantity', 0)
  return deliverySchedulingQuantity < 1
}

export const getPlanNamesFromPackages = (packages) => {
  if (!packages || packages.length === 0) return ''

  return packages
    .map(pkg => planNames[pkg.partCode] || pkg.partCode)
    .join(', ')
}
